import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Toggle } from "@/components/Toggle/Toggle";

import { SettingsBox } from "../../shell/SettingsBox";
import { AddChip, ChipStatus, StatusChip } from "../../shell/StatusChip";
import { SegmentGroup, SegmentView } from "../../shell/scoreConfigView";

interface SegmentGroupBoxProps {
	group: SegmentGroup;
	segments: SegmentView[];
	/** Partner and investor groups can be switched off as a whole; prospects cannot. */
	enabled: boolean;
	onToggleEnabled?: (enabled: boolean) => void;
	selectedKey: string | null;
	onSelect: (key: string | null) => void;
	statusOf: (segment: SegmentView) => ChipStatus;
	previewOf: (segment: SegmentView) => ReactNode;
	onAdd?: () => void;
	/** The detail card of the selected segment, when it belongs to this group. */
	children?: ReactNode;
}

const TITLE_KEY: Record<SegmentGroup, string> = {
	PROSPECT: "prospectCount",
	PARTNER: "partnerCount",
	INVESTOR: "investorCount",
};

const ADD_KEY: Record<SegmentGroup, string | null> = {
	PROSPECT: "addProspect",
	PARTNER: "addPartner",
	INVESTOR: null,
};

/**
 * One band of the Company Segments page (PREC-4055): the group toggle, the
 * count in the title, a chip per segment, the add chip, and the selected
 * segment's detail card underneath.
 */
export const SegmentGroupBox: FunctionComponent<SegmentGroupBoxProps> = ({
	group,
	segments,
	enabled,
	onToggleEnabled,
	selectedKey,
	onSelect,
	statusOf,
	previewOf,
	onAdd,
	children,
}) => {
	const { t } = useTranslation();
	const liveCount = segments.filter((segment) => segment.active).length;
	const addKey = ADD_KEY[group];

	return (
		<SettingsBox
			inactive={!enabled}
			testId={`segment-group-${group}`}
			title={
				enabled
					? t(`leadScoring.segments.${TITLE_KEY[group]}`, { count: liveCount })
					: t(
							`leadScoring.matrix.${group === "PROSPECT" ? "prospects" : group === "PARTNER" ? "partners" : "investors"}`
						)
			}
			actions={
				onToggleEnabled && (
					<Toggle
						isChecked={enabled}
						testId={`segment-group-toggle-${group}`}
						onChange={() => onToggleEnabled(!enabled)}
					/>
				)
			}
			trailing={
				!enabled ? (
					<span className="text-xs text-text-disabled">{t("leadScoring.detail.deactivated")}</span>
				) : undefined
			}
		>
			{enabled && (
				<>
					<div className="flex flex-wrap items-center gap-1.5">
						{segments.map((segment) => (
							<StatusChip
								key={segment.key}
								label={segment.name || t("leadScoring.shell.untitled")}
								preview={previewOf(segment)}
								selected={selectedKey === segment.key}
								status={statusOf(segment)}
								testId={`segment-chip-${segment.key}`}
								onClick={() => onSelect(selectedKey === segment.key ? null : segment.key)}
							/>
						))}
						{segments.length === 0 && !addKey && (
							<span className="text-xs text-text-disabled">
								{t("leadScoring.segments.nothingYet")}
							</span>
						)}
						{addKey && onAdd && (
							<AddChip
								label={t(`leadScoring.segments.${addKey}`)}
								testId={`segment-add-${group}`}
								onClick={onAdd}
							/>
						)}
					</div>
					{children}
				</>
			)}
		</SettingsBox>
	);
};
